import type { InsertAttestationCycleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import type { InsertAttestationConfigInput } from './useInsertAttestationConfigTRPC';

/**
 * Public input for the `insertAttestationCycle` mutation. Mirrors the tRPC
 * `attestationCycle.create` input so the flag-gated wrapper can forward the
 * same object to either path.
 */
export interface InsertAttestationCycleInput {
  DocumentId: string;
  AllowCarryForward: boolean;
  AttestationConfig?: InsertAttestationConfigInput | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape.
 * The legacy GraphQL mutation returned BOTH the new cycle Id and the upserted
 * config Id. The tRPC `create` returns the cycle Id; the config is upserted
 * against `AttestationConfig.ParentId`, so we surface that as the config Id.
 */
const mapTrpcResponseToGraphQL = (
  data: { Id: string },
  input: InsertAttestationCycleInput
): InsertAttestationCycleMutation => ({
  insertChildAttestationCycle: { Id: data.Id },
  insertChildAttestationConfig: input.AttestationConfig
    ? { Id: input.AttestationConfig.ParentId }
    : undefined,
});

export const useInsertAttestationCycleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.attestationCycle.create.mutationOptions({
      onSuccess: async () => {
        // The attestations Tab re-derives its view from three read families:
        // the document read (attestation config), the attestation-cycle reads,
        // and the attestation register. Invalidate all three routers so the Tab
        // reflects the new cycle + config after save — matching the Apollo path,
        // which re-fetched via those reads with no inline cache update.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.document.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestationCycle.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestation.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertAttestationCycle: async (
      input: InsertAttestationCycleInput
    ): Promise<InsertAttestationCycleMutation> => {
      const result = await mutation.mutateAsync({
        DocumentId: input.DocumentId,
        AllowCarryForward: input.AllowCarryForward,
        AttestationConfig: input.AttestationConfig,
      });

      return mapTrpcResponseToGraphQL(result, input);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
